import { memo, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { DemoChip } from '@frontend/analytics-shared';
import {
  Chart,
  LeaderboardDataValue,
  LeaderboardDataValueClickParams,
  LeaderboardProps,
  RenderDataCardParams,
} from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text, TextLink } from '@frontend/design-system';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';
import { useCallIntelLocations } from '../hooks/use-call-intel-locations';
import { useNavigateToCallRecordsWithFilter } from '../hooks/use-navigate-to-call-records-with-filter';
import { SummaryCard, SummaryCardClickHandler } from '../types';

/**
 * @deprecated TODO: Remove this type when TSS Multi changes are live
 */
type Data = {
  noOpportunities?: number;
  scheduledOpportunities?: number;
  totalAnalysedCalls?: number;
  totalOpportunities?: number;
  unscheduledOpportunities?: number;
};

type DataV2 = {
  scheduledOpportunitiesCount?: number;
  scheduledOnCallCount?: number;
  scheduledPostCallCount?: number;
  stillUnscheduledCount?: number;
  totalCallsAnalyzed?: number;
  unscheduledOnCall?: number;
};

/**
 * @deprecated TODO: Remove this type when TSS Multi changes are live
 */
type Props = {
  categorisedComparison?: boolean;
  data?: Record<string, Data>;
  isLoading?: boolean;
  onClick?: SummaryCardClickHandler;
  title: string;
};

type PropsV2 = {
  categorisedComparison?: boolean;
  data?: Record<string, DataV2>;
  isLoading?: boolean;
  onClick?: SummaryCardClickHandler;
  title: string;
};

type MetricCardProps = LeaderboardDataValue & {
  label?: string;
  isLabelClickable?: boolean;
  isSecondaryMetric?: boolean;
  onLabelClick?: () => void;
};

/**
 * @deprecated Use OpportunityConversionLeaderboardV2 instead
 */
export const OpportunityConversionLeaderboard = memo(
  ({ categorisedComparison, data = {}, isLoading, onClick, title }: Props) => {
    const { t } = useTranslation('analytics');
    const { showDemoChipAndBanner } = useCallIntelDemoFlags();
    const { filterHintText } = useActiveFilters();
    const { locations } = useCallIntelLocations();

    const chartData = useMemo(() => {
      const processedData: LeaderboardProps['data'] = Object.entries(data).map(
        ([
          locationId,
          { scheduledOpportunities = 0, totalAnalysedCalls = 0, totalOpportunities = 0, unscheduledOpportunities = 0 },
        ]) => ({
          id: locationId,
          name: locations[locationId] || locationId,
          values: {
            conversionRate: {
              primaryValue: scheduledOpportunities / totalOpportunities,
              formatter: (value) => formatters.percent(value),
            },
            unscheduled: {
              primaryValue: unscheduledOpportunities,
              secondaryValue: totalOpportunities,
            },
            callsAnalyzed: {
              primaryValue: totalAnalysedCalls,
            },
          },
        })
      );

      return processedData;
    }, [data]);

    const handleLeaderboardClick = useCallback(({ columnId, rowId }: LeaderboardDataValueClickParams) => {
      onClick?.(columnId as SummaryCard, rowId);
    }, []);

    return (
      <Chart
        isLoading={isLoading}
        labels={{
          callsAnalyzed: t('Total'),
          conversionRate: t('Scheduled Rate'),
          highestMetric: t('Highest Scheduled Rate'),
          unscheduled: t('Opportunities'),
        }}
      >
        <Chart.Header
          leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
          subtitle={filterHintText}
          title={title}
        />
        <Chart.Leaderboard
          appearance={{
            comparisonType: categorisedComparison ? 'columns' : 'rows',
            highestMetric: 'conversionRate',
          }}
          columnHeaderLabels={{
            callsAnalyzed: t('Calls Analyzed'),
            conversionRate: t('Scheduled Rate'),
            name: t('Location'),
            unscheduled: t('Unscheduled'),
          }}
          data={chartData}
          defaultSort={{
            key: 'conversionRate',
            type: 'desc',
          }}
          onClick={handleLeaderboardClick}
          trackingIdBase='ci-locations-leaderboard'
        />
      </Chart>
    );
  }
);

OpportunityConversionLeaderboard.displayName = 'OpportunityConversionLeaderboard';

// A configuration object to define the cards for the mobile view.
const cardConfig = {
  scheduledRate: ['scheduledRate', 'scheduled', 'unscheduled'],
  unscheduled: ['stillUnscheduled', 'recovered'],
};

const labels = {
  callsAnalyzed: i18next.t('Total', { ns: 'analytics' }),
  highestMetric: i18next.t('Highest Scheduled Rate', { ns: 'analytics' }),
  name: i18next.t('Location', { ns: 'analytics' }),
  recovered: i18next.t('Recovered Opportunities', { ns: 'analytics' }),
  recoveredColumn: i18next.t('Recovered', { ns: 'analytics' }),
  scheduled: i18next.t('Scheduled', { ns: 'analytics' }),
  scheduledOnCall: i18next.t('Scheduled On Call', { ns: 'analytics' }),
  scheduledRate: i18next.t('Scheduled Rate', { ns: 'analytics' }),
  stillUnscheduled: i18next.t('Still Unscheduled', { ns: 'analytics' }),
  unscheduled: i18next.t('Unscheduled', { ns: 'analytics' }),
  unscheduledOnCall: i18next.t('Unscheduled On Call', { ns: 'analytics' }),
};

const MetricCard = memo(
  ({ primaryValue, secondaryValue, isSecondaryMetric = false, label, formatter, onLabelClick }: MetricCardProps) => {
    return (
      <div>
        <Text css={styles.mainText({ isSecondaryMetric })}>
          {formatter?.(primaryValue) ?? primaryValue}
          {secondaryValue ? (
            <Text as='span' color='subdued' weight='regular' size='medium'>
              /{formatter?.(secondaryValue) ?? secondaryValue}
            </Text>
          ) : null}
        </Text>
        {onLabelClick ? (
          <TextLink onClick={onLabelClick} size='small' textAlign='left'>
            {label}
          </TextLink>
        ) : (
          <Text color='subdued' size='small'>
            {label}
          </Text>
        )}
      </div>
    );
  }
);

MetricCard.displayName = 'MetricCard';

const MobileCardGroup = memo(
  ({ columnId, rowId, values }: { columnId: string; rowId: string; values: Record<string, LeaderboardDataValue> }) => {
    const keysToRender = cardConfig[columnId as keyof typeof cardConfig] || [];

    return (
      <div css={styles.metricCardWrapper}>
        {keysToRender.map((key, index) => {
          const cardData = values[key];
          return (
            <div key={key} css={styles.metricCardItem}>
              {index === 1 && <div css={styles.divider} />}
              <MetricCard
                formatter={cardData?.formatter}
                isLabelClickable={!!cardData?.onClick}
                label={labels[key as keyof typeof labels] || key}
                onLabelClick={
                  cardData?.onClick ? () => cardData.onClick?.({ columnId, rowId, value: cardData }) : undefined
                }
                primaryValue={cardData?.primaryValue ?? 0}
                secondaryValue={cardData?.secondaryValue}
                isSecondaryMetric={index > 0}
              />
            </div>
          );
        })}
      </div>
    );
  }
);

MobileCardGroup.displayName = 'MobileCardGroup';

export const OpportunityConversionLeaderboardV2 = memo(({ data = {}, isLoading, title }: PropsV2) => {
  const { t } = useTranslation('analytics');
  const { showDemoChipAndBanner } = useCallIntelDemoFlags();
  const { filters, filterHintText } = useActiveFilters();
  const { navigateToCallRecords } = useNavigateToCallRecordsWithFilter();
  const { isSmallMode } = useIsSmallMode();

  const { locations } = useCallIntelLocations();
  const selectedLocations = filters.locations ?? [];

  const chartData = useMemo(() => {
    const processedData: LeaderboardProps['data'] = Object.entries(data).map(
      ([
        locationId,
        {
          scheduledOpportunitiesCount = 0,
          scheduledOnCallCount = 0,
          scheduledPostCallCount = 0,
          stillUnscheduledCount = 0,
          unscheduledOnCall = 0,
          totalCallsAnalyzed = 0,
        },
      ]) => {
        const allValues: Record<string, LeaderboardDataValue> = {
          scheduledRate: {
            primaryValue: scheduledOpportunitiesCount > 0 ? scheduledOnCallCount / scheduledOpportunitiesCount : 0,
            formatter: (value) => formatters.percent(value),
          },
          scheduledOnCall: {
            clickTarget: 'label',
            primaryValue: scheduledOnCallCount,
            onClick: () =>
              navigateToCallRecords({
                locations: [locationId],
                schedulingOutcomeV2s: [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL],
              }),
          },
          unscheduledOnCall: {
            clickTarget: 'label',
            primaryValue: unscheduledOnCall,
            onClick: () =>
              navigateToCallRecords({
                locations: [locationId],
                schedulingOutcomeV2s: [
                  CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED,
                  CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL,
                ],
              }),
          },
          stillUnscheduled: {
            clickTarget: 'label',
            primaryValue: stillUnscheduledCount,
            secondaryValue: unscheduledOnCall,
            onClick: () =>
              navigateToCallRecords({
                locations: [locationId],
                schedulingOutcomeV2s: [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED],
              }),
          },
          recovered: {
            clickTarget: 'label',
            primaryValue: scheduledPostCallCount,
            secondaryValue: unscheduledOnCall,
            onClick: () =>
              navigateToCallRecords({
                locations: [locationId],
                schedulingOutcomeV2s: [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL],
              }),
          },
        };

        return {
          id: locationId,
          name: locations[locationId] || locationId,
          values: isSmallMode
            ? {
                ...allValues,
                scheduled: allValues.scheduledOnCall,
                unscheduled: allValues.unscheduledOnCall,
              }
            : allValues,
          bottomElement: (
            <Text
              size='small'
              color='subdued'
              css={css`
                margin-top: ${theme.spacing(0.5)};
              `}
            >
              <Text as='span' size='small' color='subdued' weight='bold'>
                {totalCallsAnalyzed}{' '}
              </Text>
              {t('Calls Analyzed')}
            </Text>
          ),
        };
      }
    );

    return processedData;
  }, [data, isSmallMode, locations, navigateToCallRecords, t]);

  const renderMobileCard = useCallback(({ columnId, data, rowId }: RenderDataCardParams) => {
    const locationRow = data.find((item) => item.id === rowId);
    if (!locationRow) return null;

    return <MobileCardGroup columnId={columnId} rowId={rowId} values={locationRow.values} />;
  }, []);

  return (
    <Chart isLoading={isLoading} labels={labels}>
      <Chart.HeaderV2
        title={
          <Chart.HeaderV2.Title leftElement={showDemoChipAndBanner ? <DemoChip /> : null}>{title}</Chart.HeaderV2.Title>
        }
        action={<Chart.HeaderV2.FilterText filterHintText={filterHintText} />}
      />
      {isSmallMode ? (
        <Chart.Leaderboard
          appearance={{
            comparisonType: 'rows',
            ...(selectedLocations.length > 1 && { highestMetric: 'scheduledRate' }),
            stripeOddRows: true,
          }}
          columnHeaderLabels={{
            scheduledRate: t('Conversion During Call'),
            unscheduled: t('Active Opportunities'),
          }}
          data={chartData}
          defaultSort={{
            key: 'scheduledRate',
            type: 'desc',
          }}
          isPivotMode
          pivotMetricKeys={Object.keys(cardConfig)}
          trackingIdBase='ci-locations-leaderboard'
          renderDataCard={renderMobileCard}
        />
      ) : (
        <Chart.Leaderboard
          appearance={{
            comparisonType: 'rows',
            ...(selectedLocations.length > 1 && { highestMetric: 'scheduledRate' }),
            stripeOddRows: true,
            isBorderless: true,
          }}
          columnHeaderLabels={{
            ...labels,
            scheduledOnCall: labels.scheduled,
            unscheduledOnCall: labels.unscheduled,
            recovered: labels.recoveredColumn,
          }}
          data={chartData}
          defaultSort={{
            key: 'scheduledRate',
            type: 'desc',
          }}
          trackingIdBase='ci-locations-leaderboard'
        />
      )}
    </Chart>
  );
});

OpportunityConversionLeaderboardV2.displayName = 'OpportunityConversionLeaderboardV2';

const styles = {
  mainText: ({ isSecondaryMetric }: { isSecondaryMetric: boolean }) => css`
    font-size: ${isSecondaryMetric ? theme.font.size.large : theme.font.size.h2};
    font-weight: ${theme.font.weight.bold};
    line-height: ${theme.spacing(3)};
    color: ${isSecondaryMetric ? theme.font.colors.subdued : theme.font.colors.default};
  `,
  metricCardWrapper: css`
    display: flex;
    gap: ${theme.spacing(1.5)};
    padding: ${theme.spacing(0, 1.5, 1, 1.5)};
  `,

  metricCardItem: css`
    display: flex;
    gap: ${theme.spacing(1.5)};
  `,

  divider: css`
    height: 100%;
    border-left: 1px solid ${theme.colors.neutral20};
  `,
};
